import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowLeft, Camera } from 'lucide-react';
import { Button, Card, CardContent, Progress } from '@/components/ui';
import { StatusIndicator } from '@/components/domain';
import { BrandMark } from '@/components/shared/BrandMark';
import { ThemeToggle } from '@/components/shared/ThemeToggle';
import { useAuthStore, useUIStore } from '@/stores';
import { apiClient } from '@/api/client';
import { cn } from '@/utils/cn';

interface ConsentItem {
  id: string;
  title: string;
  description: string;
  required: boolean;
}

const consentItems: ConsentItem[] = [
  {
    id: 'gazeTracking',
    title: 'Gaze Tracking Monitoring',
    description: 'I agree to real-time gaze tracking during the exam using on-device WebGazer in my browser. Gaze is processed on my device; video frames are not uploaded for gaze inference.',
    required: true
  },
  {
    id: 'behavioralAnalysis',
    title: 'Behavioral Analysis',
    description: 'I consent to behavioral pattern analysis including attention tracking and integrity flags for exam integrity.',
    required: true
  },
  {
    id: 'cameraAccess',
    title: 'Camera Access Permission',
    description: 'I grant permission for QuizSecure to access my camera for continuous monitoring throughout the exam session.',
    required: true
  },
  {
    id: 'dataStorage',
    title: 'Session Data Storage',
    description: 'I understand that exam session data, including monitoring metrics and responses, will be stored securely and used for academic evaluation only.',
    required: true
  },
  {
    id: 'academicIntegrity',
    title: 'Academic Integrity Agreement',
    description: 'I agree to maintain academic integrity and understand that any detected violations will be reported to my instructor.',
    required: true
  }
];

const progressSteps = [
  { id: 'login', label: 'Login', completed: true },
  { id: 'consent', label: 'Consent & Verification', active: true },
  { id: 'exam', label: 'Exam', upcoming: true }
];

export const ConsentPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, giveConsent } = useAuthStore();
  const { showNotification } = useUIStore();
  
  const [consentGiven, setConsentGiven] = useState<Record<string, boolean>>({});
  const [cameraStatus, setCameraStatus] = useState<'idle' | 'requesting' | 'granted' | 'denied'>('idle');
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [monitoringReady, setMonitoringReady] = useState<boolean | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Check if user is authenticated
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Check if consent already given
    const existingConsent = localStorage.getItem('consent_given');
    if (existingConsent === 'true') {
      navigate('/student');
    }
  }, [user, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const health = await apiClient.healthCheck() as {
          monitoring_ready?: boolean;
          monitoring_mode?: string;
          eth_xgaze?: string;
          data?: { monitoring_ready?: boolean; eth_xgaze?: string; monitoring_mode?: string };
        };
        const ready =
          health.monitoring_ready === true ||
          health.data?.monitoring_ready === true ||
          health.monitoring_mode === 'webgazer' ||
          health.data?.monitoring_mode === 'webgazer';
        if (!cancelled) setMonitoringReady(ready);
      } catch {
        if (!cancelled) setMonitoringReady(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Attach stream after the <video> mounts (status → granted remounts the element).
  useEffect(() => {
    const video = videoRef.current;
    const mediaStream = stream;
    if (!video || !mediaStream || cameraStatus !== 'granted') {
      return;
    }

    video.srcObject = mediaStream;
    const play = () => {
      video.play().catch((error) => {
        console.error('Video play failed:', error);
      });
    };

    if (video.readyState >= 1) {
      play();
    } else {
      video.onloadedmetadata = play;
    }

    return () => {
      video.onloadedmetadata = null;
    };
  }, [stream, cameraStatus]);

  const handleConsentChange = (itemId: string, checked: boolean) => {
    setConsentGiven(prev => ({
      ...prev,
      [itemId]: checked
    }));
  };

  const requestCameraAccess = async () => {
    setCameraStatus('requesting');
    
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 30 }
        },
        audio: false
      });

      streamRef.current = mediaStream;
      setStream(mediaStream);
      setCameraStatus('granted');

      showNotification({
        type: 'success',
        title: 'Camera Access Granted',
        message: 'Camera verification successful!'
      });

      // Auto-check camera consent
      setConsentGiven(prev => ({
        ...prev,
        cameraAccess: true
      }));

    } catch (error) {
      console.error('Camera access denied:', error);
      setCameraStatus('denied');
      
      showNotification({
        type: 'error',
        title: 'Camera Access Required',
        message: 'Camera permission is required to proceed with the exam.'
      });
    }
  };

  const stopCamera = () => {
    const activeStream = streamRef.current ?? stream;
    if (activeStream) {
      activeStream.getTracks().forEach(track => track.stop());
    }
    streamRef.current = null;
    setStream(null);
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraStatus('idle');
  };

  const allRequiredConsentsGiven = () => {
    return consentItems
      .filter(item => item.required)
      .every(item => consentGiven[item.id]);
  };

  const handleSubmit = async () => {
    if (!allRequiredConsentsGiven()) {
      showNotification({
        type: 'error',
        title: 'Consent Required',
        message: 'Please agree to all required terms before proceeding.'
      });
      return;
    }

    if (cameraStatus !== 'granted') {
      showNotification({
        type: 'error',
        title: 'Camera Verification Required',
        message: 'Please verify your camera access before proceeding.'
      });
      return;
    }

    if (monitoringReady !== true) {
      showNotification({
        type: 'error',
        title: 'API Unavailable',
        message: 'Cannot continue until the QuizSecure API is reachable. Gaze still runs on your device after you start.',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await giveConsent(consentGiven);
      
      showNotification({
        type: 'success',
        title: 'Consent Recorded',
        message: 'Proceeding to exam interface...'
      });

      // Clean up camera stream
      stopCamera();
      
      // Navigate to exam
      navigate('/student');
      
    } catch (error) {
      console.error('Consent submission error:', error);
      
      showNotification({
        type: 'error',
        title: 'Submission Failed',
        message: 'Failed to record consent. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const goBack = () => {
    stopCamera();
    navigate('/login');
  };

  // Cleanup on unmount — use streamRef so we don't stale-close over null
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  return (
    <div className="min-h-screen animate-page-enter bg-surface-canvas font-sans text-content-primary">
      {/* Header */}
      <nav className="border-b border-subtle bg-surface-canvas">
        <div className="mx-auto flex max-w-page flex-col gap-4 px-4 py-4 sm:px-6 md:flex-row md:items-center md:justify-between lg:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <BrandMark frameClassName="size-9 sm:size-9" />
            <span className="font-display text-xl font-normal">QuizSecure</span>
            <span className="hidden h-5 w-px bg-hairline sm:block" aria-hidden="true" />
            <span className="hidden text-sm text-content-muted sm:block">Consent &amp; verification</span>
          </div>
          <div className="flex items-center justify-between gap-4 md:justify-end">
            <div className="min-w-0 text-sm text-content-muted">
              Welcome, <span className="font-medium text-content-primary">{user?.name || 'Student'}</span>
            </div>
            <ThemeToggle />
            <Button variant="ghost" onClick={goBack}>
              <ArrowLeft className="size-4" aria-hidden="true" />
              Back to Login
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="mx-auto max-w-page px-4 py-10 sm:px-6 lg:px-8 lg:py-12">
        <div className="mb-8 max-w-2xl">
          <p className="text-caption font-medium uppercase tracking-[0.16em] text-content-muted">
            Exam setup
          </p>
          <h1 className="mt-3 font-display text-heading font-normal">Consent &amp; verification</h1>
          <p className="mt-3 text-content-secondary">
            Review the monitoring terms and verify your camera before entering the exam.
          </p>
        </div>

        {/* Progress Indicator */}
        <nav className="mb-8 rounded-card border border-subtle bg-surface-card px-4 py-5 sm:px-6" aria-label="Exam setup progress">
          <ol className="grid grid-cols-3 gap-3 text-sm sm:gap-6">
            {progressSteps.map((step, index) => (
              <li
                key={step.id}
                className={cn(
                  'min-w-0 border-t-2 pt-3',
                  step.completed && 'border-state-nominal',
                  step.active && 'border-ink',
                  step.upcoming && 'border-hairline'
                )}
                aria-current={step.active ? 'step' : undefined}
              >
                <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center">
                  <span className={cn(
                    'flex size-8 shrink-0 items-center justify-center rounded-control border font-medium',
                    step.completed && 'border-state-nominal bg-state-nominal-soft text-state-nominal',
                    step.active && 'border-ink bg-ink text-parchment',
                    step.upcoming && 'border-hairline bg-surface-nested text-content-muted'
                  )}>
                    {step.completed ? '✓' : index + 1}
                  </span>
                  <span className={cn(
                    'text-xs leading-5 sm:text-sm',
                    step.completed && 'text-state-nominal',
                    step.active && 'font-medium text-content-primary',
                    step.upcoming && 'text-content-muted'
                  )}>
                    {step.label}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        </nav>

        {/* Two Column Layout */}
        <div className="grid gap-8 lg:grid-cols-2">
          {/* Left Column - Consent Form */}
          <Card size="lg" className="animate-rise-in">
            <CardContent className="p-6 sm:p-8">
              <div className="mb-6">
                <h2 className="font-display text-heading-sm font-normal">
                  Exam Consent Agreement
                </h2>
                <p className="mt-3 text-content-secondary">
                  Please read and agree to the following terms before proceeding to your exam.
                  All monitoring is conducted to ensure academic integrity.
                </p>
              </div>

              <div className="space-y-4">
                {consentItems.map((item) => {
                  const isChecked = Boolean(consentGiven[item.id]);
                  return (
                  <div
                    key={item.id}
                    className={cn(
                      'rounded-control border p-4 transition-[border-color,background-color] duration-150',
                      isChecked
                        ? 'border-ink/40 bg-state-nominal-soft'
                        : 'border-subtle bg-surface-nested'
                    )}
                  >
                    <label className="flex cursor-pointer items-start gap-3">
                      <span className="relative mt-0.5 inline-flex size-5 shrink-0 items-center justify-center">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => handleConsentChange(item.id, e.target.checked)}
                          aria-required={item.required}
                          className="peer absolute inset-0 z-10 size-5 cursor-pointer opacity-0"
                        />
                        <span
                          aria-hidden="true"
                          className={cn(
                            'pointer-events-none flex size-5 items-center justify-center rounded-[0.35rem] border-2 transition-colors',
                            isChecked
                              ? 'border-ink bg-ink text-parchment'
                              : 'border-hairline bg-surface-card'
                          )}
                        >
                          {isChecked && (
                            <Check className="size-3.5" strokeWidth={2.5} aria-hidden="true" />
                          )}
                        </span>
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="mb-1 flex flex-wrap items-center gap-2 font-medium text-content-primary">
                          <span>{item.title}</span>
                          {item.required && (
                            <span className="text-caption font-medium uppercase tracking-[0.12em] text-content-muted">
                              Required
                            </span>
                          )}
                          {isChecked && (
                            <span className="text-caption font-medium uppercase tracking-[0.12em] text-state-nominal">
                              Agreed
                            </span>
                          )}
                        </div>
                        <div className="text-sm leading-6 text-content-secondary">
                          {item.description}
                        </div>
                      </div>
                    </label>
                  </div>
                  );
                })}
              </div>

              {/* Consent Progress */}
              <div className="mt-6">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm text-content-muted">Consent progress</span>
                  <span className="text-sm text-content-muted">
                    {Object.values(consentGiven).filter(Boolean).length} / {consentItems.length}
                  </span>
                </div>
                <Progress
                  value={(Object.values(consentGiven).filter(Boolean).length / consentItems.length) * 100}
                  variant={allRequiredConsentsGiven() ? 'success' : 'default'}
                />
              </div>
            </CardContent>
          </Card>

          {/* Right Column - Camera Verification */}
          <Card size="lg" className="animate-rise-in">
            <CardContent className="p-6 sm:p-8">
              <div className="mb-6">
                <h2 className="font-display text-heading-sm font-normal">
                  Camera Verification
                </h2>
                <p className="mt-3 text-content-secondary">
                  Verify your camera is working properly for exam monitoring. 
                  This is required for the gaze tracking system.
                </p>
              </div>

              {/* Camera Preview */}
              <div className="mb-6 rounded-card border border-subtle bg-surface-nested p-4">
                <div className="mb-4 flex aspect-video items-center justify-center overflow-hidden rounded-control bg-obsidian">
                  {cameraStatus === 'granted' ? (
                    <video
                      ref={videoRef}
                      className="h-full w-full rounded-control object-cover"
                      autoPlay
                      muted
                      playsInline
                      aria-label="Live camera verification preview"
                    />
                  ) : (
                    <div className="px-4 text-center text-paper-white">
                      <Camera className="mx-auto mb-3 size-8 opacity-70" aria-hidden="true" />
                      <div className="mb-2 font-display text-lg font-normal">
                        {cameraStatus === 'requesting' ? 'Requesting Access...' : 
                         cameraStatus === 'denied' ? 'Camera Access Denied' : 
                         'Camera Preview'}
                      </div>
                      <div className="text-sm text-paper-white/70">Camera preview will appear here</div>
                    </div>
                  )}
                </div>

                <div className="mb-4 flex items-center justify-between">
                  <StatusIndicator
                    status={
                      cameraStatus === 'granted' ? 'online' :
                      cameraStatus === 'requesting' ? 'connecting' :
                      cameraStatus === 'denied' ? 'error' : 'offline'
                    }
                    label={
                      cameraStatus === 'granted' ? 'Camera Active' :
                      cameraStatus === 'requesting' ? 'Requesting Access' :
                      cameraStatus === 'denied' ? 'Access Denied' : 'Camera Inactive'
                    }
                  />
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={requestCameraAccess}
                    disabled={cameraStatus === 'requesting' || cameraStatus === 'granted'}
                    loading={cameraStatus === 'requesting'}
                  >
                    {cameraStatus === 'granted' ? 'Camera Active' : 'Test Camera'}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={stopCamera}
                    disabled={cameraStatus !== 'granted'}
                  >
                    Stop Camera
                  </Button>
                </div>
              </div>

              {/* System Requirements */}
              <div className="mb-6 rounded-card border border-subtle bg-surface-nested p-4">
                <h3 className="font-display text-heading-sm font-normal">System Requirements</h3>
                <div className="mt-4 divide-y divide-subtle text-sm">
                  <div className="flex items-center justify-between gap-4 py-3 first:pt-0">
                    <span>Camera Access</span>
                    <StatusIndicator
                      status={cameraStatus === 'granted' ? 'online' : 'offline'}
                      label={cameraStatus === 'granted' ? 'Ready' : 'Required'}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-4 py-3">
                    <span>On-device gaze (WebGazer)</span>
                    <StatusIndicator
                      status={
                        monitoringReady === true
                          ? 'ready'
                          : monitoringReady === false
                            ? 'critical'
                            : 'offline'
                      }
                      label={
                        monitoringReady === true
                          ? 'Ready'
                          : monitoringReady === false
                            ? 'API down'
                            : 'Checking…'
                      }
                      pulse={false}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-4 py-3 last:pb-0">
                    <span>Network Connection</span>
                    <StatusIndicator
                      status={typeof navigator !== 'undefined' && navigator.onLine ? 'nominal' : 'offline'}
                      label={typeof navigator !== 'undefined' && navigator.onLine ? 'Nominal' : 'Offline'}
                      pulse={false}
                    />
                  </div>
                </div>
              </div>

              {/* Proceed Button */}
              <Button
                variant="primary"
                size="lg"
                onClick={handleSubmit}
                disabled={
                  !allRequiredConsentsGiven() ||
                  cameraStatus !== 'granted' ||
                  monitoringReady !== true
                }
                loading={isSubmitting}
                className="w-full"
              >
                {isSubmitting
                  ? 'Processing...'
                  : monitoringReady === false
                    ? 'API unavailable'
                    : 'Continue to exam setup'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};